import { useEditor, EditorContent, BubbleMenu, FloatingMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect, useMemo } from 'react';
import { WikiLink } from './extensions/WikiLink.js';
import { SlashChildPage } from './extensions/SlashChildPage.js';
import type { DocumentContent } from '@synapse/shared/schemas';

interface Props {
  documentId: string;
  initial: DocumentContent | null;
  onChange: (next: DocumentContent) => void;
}

export function DocumentEditor({ documentId, initial, onChange }: Props) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: "Digite '/' para comandos ou comece a escrever..." }),
      WikiLink,
      SlashChildPage
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: initial ?? { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor }) => {
      const json = editor.getJSON() as DocumentContent;
      onChange(json);
    },
  });

  // Atualiza conteúdo quando documentId muda (navegação entre docs).
  useEffect(() => {
    if (!editor || !initial) return;
    editor.commands.setContent(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  if (!editor) return <div className="text-zinc-500 text-sm">carregando editor…</div>;

  return (
    <div className="prose prose-invert max-w-none relative pb-32">
      {editor && (
        <BubbleMenu editor={editor} tippyOptions={{ duration: 100 }} className="flex overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/95 shadow-xl backdrop-blur-sm">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`px-3 py-2 text-sm font-semibold hover:bg-zinc-800 transition-colors ${editor.isActive('bold') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
          >
            B
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`px-3 py-2 text-sm italic hover:bg-zinc-800 transition-colors ${editor.isActive('italic') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
          >
            I
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`px-3 py-2 text-sm line-through hover:bg-zinc-800 transition-colors ${editor.isActive('strike') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
          >
            S
          </button>
          <div className="w-[1px] bg-zinc-800 mx-1 my-2" />
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`px-3 py-2 text-sm font-bold hover:bg-zinc-800 transition-colors ${editor.isActive('heading', { level: 2 }) ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
          >
            H2
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`px-3 py-2 text-sm font-bold hover:bg-zinc-800 transition-colors ${editor.isActive('heading', { level: 3 }) ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
          >
            H3
          </button>
        </BubbleMenu>
      )}

      {editor && (
        <FloatingMenu editor={editor} tippyOptions={{ duration: 100 }} className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900/95 p-1 shadow-xl backdrop-blur-sm">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`rounded px-2 py-1.5 text-xs hover:bg-zinc-800 transition-colors flex items-center gap-1 ${editor.isActive('bulletList') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
            title="Marcadores"
          >
            <span className="material-symbols-outlined text-[16px]">format_list_bulleted</span>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`rounded px-2 py-1.5 text-xs hover:bg-zinc-800 transition-colors flex items-center gap-1 ${editor.isActive('orderedList') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
            title="Lista numerada"
          >
            <span className="material-symbols-outlined text-[16px]">format_list_numbered</span>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleTaskList().run()}
            className={`rounded px-2 py-1.5 text-xs hover:bg-zinc-800 transition-colors flex items-center gap-1 ${editor.isActive('taskList') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
            title="Checklist"
          >
            <span className="material-symbols-outlined text-[16px]">check_box</span>
          </button>
          <div className="w-[1px] bg-zinc-800 mx-1 my-1" />
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`rounded px-2 py-1.5 text-xs hover:bg-zinc-800 transition-colors flex items-center gap-1 ${editor.isActive('blockquote') ? 'text-violet-400 bg-zinc-800/50' : 'text-zinc-300'}`}
            title="Citação"
          >
            <span className="material-symbols-outlined text-[16px]">format_quote</span>
          </button>
        </FloatingMenu>
      )}

      <EditorContent editor={editor} />
    </div>
  );
}
